import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSites, SitePicker } from "@/components/wp/SitePicker";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Pencil, RefreshCw, ShoppingCart } from "lucide-react";

const ORDER_STATUSES = ["pending", "processing", "on-hold", "completed", "cancelled", "refunded", "failed"];
const ORDER_BADGE = {
  processing: "bg-blue-50 text-blue-800 border-blue-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  "on-hold": "bg-amber-50 text-amber-800 border-amber-200",
  cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200",
  refunded: "bg-zinc-100 text-zinc-500 border-zinc-200",
  failed: "bg-red-50 text-red-700 border-red-200",
};
const money = (v, c = "AUD") => `$${Number(v || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`;

function OrdersTab({ siteId }) {
  const [status, setStatus] = useState("any");
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    if (!siteId) return;
    try {
      setData((await api.get(`/wp/sites/${siteId}/woo/orders`, { params: { status } })).data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load orders"); setData({ rows: [] }); }
  }, [siteId, status]);
  useEffect(() => { setData(null); load(); }, [load]);

  const openOrder = async (o) => {
    try { setDetail((await api.get(`/wp/sites/${siteId}/woo/orders/${o.id}`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setOrderStatus = async (newStatus) => {
    setBusy("status");
    try {
      await api.put(`/wp/sites/${siteId}/woo/orders/${detail.id}`, { status: newStatus });
      toast.success(`Order #${detail.number} → ${newStatus}`);
      setDetail({ ...detail, status: newStatus }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">All statuses</SelectItem>
            {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-zinc-400" /> : (
        <Card><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
              <th className="p-3">Order</th><th className="p-3">Customer</th>
              <th className="p-3">Date</th><th className="p-3">Items</th>
              <th className="p-3 text-right">Total</th><th className="p-3">Status</th>
            </tr></thead>
            <tbody>
              {data.rows.map((o) => (
                <tr key={o.id} className="border-b hover:bg-blue-50/40 cursor-pointer"
                  data-testid={`order-row-${o.id}`} onClick={() => openOrder(o)}>
                  <td className="p-3 font-medium text-[#002FA7]">#{o.number}</td>
                  <td className="p-3">{o.customer || o.email || "—"}</td>
                  <td className="p-3 text-xs text-zinc-500">{(o.date_created || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3">{o.items}</td>
                  <td className="p-3 text-right">{money(o.total)}</td>
                  <td className="p-3"><Badge variant="outline" className={ORDER_BADGE[o.status] || ""}>{o.status}</Badge></td>
                </tr>
              ))}
              {!data.rows.length && <tr><td colSpan={6} className="p-8 text-center text-zinc-400">No orders.</td></tr>}
            </tbody>
          </table>
        </CardContent></Card>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="order-detail-dialog">
          <DialogHeader><DialogTitle>Order #{detail?.number}</DialogTitle>
            <DialogDescription>Live order details from the client store.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={ORDER_BADGE[detail.status] || ""}>{detail.status}</Badge>
                <span className="text-zinc-500">{(detail.date_created || "").slice(0, 16).replace("T", " ")}</span>
                <span className="font-bold ml-auto">{money(detail.total)}</span>
              </div>
              <div className="text-zinc-600">
                {detail.billing?.first_name} {detail.billing?.last_name} · {detail.billing?.email}
                {detail.payment_method_title && <> · {detail.payment_method_title}</>}
              </div>
              <div className="border rounded-md divide-y">
                {detail.line_items.map((li, i) => (
                  <div key={`${li.name}-${li.sku || i}`} className="flex justify-between p-2">
                    <span>{li.quantity}× {li.name}</span><span>{money(li.total)}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 items-center">
                <span className="text-xs text-zinc-500">Change status:</span>
                <Select onValueChange={setOrderStatus} disabled={busy === "status"}>
                  <SelectTrigger className="w-40" data-testid="order-status-change"><SelectValue placeholder={detail.status} /></SelectTrigger>
                  <SelectContent>
                    {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {busy === "status" && <Loader2 className="h-4 w-4 animate-spin" />}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProductsTab({ siteId }) {
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!siteId) return;
    try {
      setData((await api.get(`/wp/sites/${siteId}/woo/products`, { params: { search } })).data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load products"); setData({ rows: [] }); }
  }, [siteId, search]);
  useEffect(() => { setData(null); load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/wp/sites/${siteId}/woo/products/${edit.id}`, {
        regular_price: edit.regular_price || null,
        sale_price: edit.sale_price ?? null,
        stock_quantity: edit.stock_quantity === "" || edit.stock_quantity == null
          ? null : Number(edit.stock_quantity),
        stock_status: edit.stock_status || null,
        status: edit.status || null,
      });
      toast.success("Product updated on the live site");
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center">
        <Input placeholder="Search products…" className="w-64" value={search}
          data-testid="products-search-input" onChange={(e) => setSearch(e.target.value)} />
        <Button variant="outline" size="icon" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-zinc-400" /> : (
        <Card><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
              <th className="p-3">Product</th><th className="p-3">SKU</th>
              <th className="p-3 text-right">Price</th><th className="p-3">Stock</th>
              <th className="p-3">Status</th><th className="p-3 text-right">Edit</th>
            </tr></thead>
            <tbody>
              {data.rows.map((p) => (
                <tr key={p.id} className="border-b" data-testid={`product-row-${p.id}`}>
                  <td className="p-3 font-medium">{p.name}</td>
                  <td className="p-3 text-xs">{p.sku || "—"}</td>
                  <td className="p-3 text-right">
                    {p.sale_price ? (
                      <><span className="line-through text-zinc-400 mr-1">{money(p.regular_price)}</span>{money(p.sale_price)}</>
                    ) : money(p.regular_price || p.price)}
                  </td>
                  <td className="p-3">
                    <Badge variant="outline" className={p.stock_status === "instock"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-red-50 text-red-700 border-red-200"}>
                      {p.stock_status}{p.stock_quantity != null ? ` (${p.stock_quantity})` : ""}
                    </Badge>
                  </td>
                  <td className="p-3">{p.status}</td>
                  <td className="p-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEdit({ ...p })}
                      data-testid={`product-edit-${p.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {!data.rows.length && <tr><td colSpan={6} className="p-8 text-center text-zinc-400">No products.</td></tr>}
            </tbody>
          </table>
        </CardContent></Card>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="product-edit-dialog">
          <DialogHeader><DialogTitle>{edit?.name}</DialogTitle>
            <DialogDescription>Price and stock changes save directly to the live store.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-medium text-zinc-500 mb-1">Regular price</div>
                <Input value={edit.regular_price || ""} data-testid="product-price-input"
                  onChange={(e) => setEdit({ ...edit, regular_price: e.target.value })} />
              </div>
              <div>
                <div className="text-xs font-medium text-zinc-500 mb-1">Sale price (blank = none)</div>
                <Input value={edit.sale_price || ""}
                  onChange={(e) => setEdit({ ...edit, sale_price: e.target.value })} />
              </div>
              <div>
                <div className="text-xs font-medium text-zinc-500 mb-1">Stock quantity</div>
                <Input type="number" value={edit.stock_quantity ?? ""}
                  onChange={(e) => setEdit({ ...edit, stock_quantity: e.target.value })} />
              </div>
              <div>
                <div className="text-xs font-medium text-zinc-500 mb-1">Stock status</div>
                <Select value={edit.stock_status}
                  onValueChange={(v) => setEdit({ ...edit, stock_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instock">In stock</SelectItem>
                    <SelectItem value="outofstock">Out of stock</SelectItem>
                    <SelectItem value="onbackorder">On backorder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="product-save-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save to site
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CustomersTab({ siteId }) {
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    if (!siteId) return;
    try {
      setData((await api.get(`/wp/sites/${siteId}/woo/customers`, { params: { search } })).data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load customers"); setData({ rows: [] }); }
  }, [siteId, search]);
  useEffect(() => { setData(null); load(); }, [load]);

  return (
    <div className="space-y-3">
      <Input placeholder="Search customers…" className="w-64" value={search}
        onChange={(e) => setSearch(e.target.value)} />
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-zinc-400" /> : (
        <Card><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
              <th className="p-3">Customer</th><th className="p-3">Email</th>
              <th className="p-3 text-right">Orders</th><th className="p-3 text-right">Total spent</th>
            </tr></thead>
            <tbody>
              {data.rows.map((c) => (
                <tr key={c.id} className="border-b" data-testid={`customer-row-${c.id}`}>
                  <td className="p-3 font-medium">{c.name || "—"}</td>
                  <td className="p-3">{c.email}</td>
                  <td className="p-3 text-right">{c.orders_count}</td>
                  <td className="p-3 text-right">{money(c.total_spent)}</td>
                </tr>
              ))}
              {!data.rows.length && <tr><td colSpan={4} className="p-8 text-center text-zinc-400">No customers.</td></tr>}
            </tbody>
          </table>
        </CardContent></Card>
      )}
    </div>
  );
}

function ReportsTab({ siteId }) {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!siteId) return;
    setData(null);
    api.get(`/wp/sites/${siteId}/woo/reports`, { params: { period } })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(e.response?.data?.detail || "Failed to load reports"); setData({}); });
  }, [siteId, period]);

  const s = data?.sales;
  return (
    <div className="space-y-4">
      <Select value={period} onValueChange={setPeriod}>
        <SelectTrigger className="w-36" data-testid="reports-period-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="week">This week</SelectItem>
          <SelectItem value="month">This month</SelectItem>
          <SelectItem value="last_month">Last month</SelectItem>
          <SelectItem value="year">This year</SelectItem>
        </SelectContent>
      </Select>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-zinc-400" /> : s ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="reports-cards">
            {[["Gross sales", money(s.total_sales)], ["Net sales", money(s.net_sales)],
              ["Orders", s.total_orders], ["Items sold", s.total_items],
              ["Customers", s.total_customers]].map(([l, v]) => (
              <Card key={l}><CardContent className="p-4">
                <div className="text-xs text-zinc-500">{l}</div>
                <div className="text-xl font-bold">{v}</div>
              </CardContent></Card>
            ))}
          </div>
          {data.orders_totals && (
            <Card><CardContent className="p-4">
              <div className="font-semibold text-sm mb-2">Orders by status (all time)</div>
              <div className="flex flex-wrap gap-2">
                {data.orders_totals.map((t) => (
                  <Badge key={t.slug} variant="outline" className={ORDER_BADGE[t.slug] || ""}>
                    {t.name}: {t.total}
                  </Badge>
                ))}
              </div>
            </CardContent></Card>
          )}
        </>
      ) : (
        <div className="text-sm text-zinc-400">No sales data returned for this period.</div>
      )}
    </div>
  );
}

export default function WooCommercePage() {
  const { sites, siteId, pick } = useSites();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "orders";

  return (
    <div className="space-y-5" data-testid="woocommerce-page">
      <div className="flex items-center gap-3">
        <ShoppingCart className="h-6 w-6 text-[#002FA7]" />
        <div>
          <h1 className="text-2xl font-bold">WooCommerce API</h1>
          <p className="text-sm text-zinc-500">
            Orders, products, customers and sales reports for client stores. Requires per-site
            REST keys (WooCommerce → Settings → Advanced → REST API).
          </p>
        </div>
      </div>
      <SitePicker sites={sites} siteId={siteId} onPick={pick} />
      {siteId && (
        <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
          <TabsList>
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          </TabsList>
          <TabsContent value="orders"><OrdersTab siteId={siteId} /></TabsContent>
          <TabsContent value="products"><ProductsTab siteId={siteId} /></TabsContent>
          <TabsContent value="customers"><CustomersTab siteId={siteId} /></TabsContent>
          <TabsContent value="reports"><ReportsTab siteId={siteId} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
